import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { TableRowSelection } from 'antd/es/table/interface';
import {
    Chip,
    DataTable,
    DropdownMenu,
    StatusDot,
    type ColumnConfig,
    type DropdownMenuItem,
    type StatusConfig,
} from '@/components/ui';
import type { RoleMapGroup, RoleMapSortField } from './types';
import type { RoleMapTableVM } from './useRoleMapTable';

function SortIcon({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
    if (!active) {
        return (
            <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="var(--color-control-ring)"
                strokeWidth={2.2}
                aria-hidden="true"
            >
                <path d="m7 10 5-5 5 5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="m7 14 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    }
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth={2.4}
            aria-hidden="true"
        >
            <path
                d="m7 10 5-5 5 5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={direction === 'asc' ? 1 : 0.28}
            />
            <path
                d="m7 14 5 5 5-5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={direction === 'desc' ? 1 : 0.28}
            />
        </svg>
    );
}

interface HeaderCellProps {
    label: string;
    /** Omit to render a non-sortable header (chips + status columns). */
    field?: RoleMapSortField;
    vm: RoleMapTableVM;
    /** `search` focuses the toolbar input; the menu keys open that filter. */
    tool?: 'search' | 'apps' | 'status';
    toolTitle?: string;
}

/**
 * EAA-1884: kept verbatim as the `ColumnConfig.title` of each `DataTable`
 * column below rather than switched to the kit's own `sorter`/`filter`
 * mechanisms. Both would fight this screen's actual design: the sort click
 * cycle is driven by `vm.toggleSort` directly (not the ordering-string
 * parsing `DataTable`'s `onSortChange` expects), and the header's "tool"
 * funnel icon is a SECOND TRIGGER for the exact same `RoleMapToolbar` filter
 * popovers (see `useRoleMapTable`'s `openFilter` doc comment) — `DataTable`'s
 * native filter icon would pop a different, unrelated dropdown next to it.
 */
function HeaderCell({ label, field, vm, tool, toolTitle }: HeaderCellProps) {
    const active = field !== undefined && vm.sortField === field;
    return (
        <span className={active ? 'rm-th is-active' : 'rm-th'}>
            {field === undefined ? (
                label
            ) : (
                <button type="button" className="rm-th__sort" onClick={() => vm.toggleSort(field)}>
                    {label}
                    <SortIcon active={active} direction={vm.sortDirection} />
                </button>
            )}
            {tool && (
                <>
                    <span className="rm-th__spacer" />
                    <button
                        type="button"
                        className="rm-th__tool"
                        title={toolTitle}
                        aria-label={toolTitle ?? label}
                        onClick={() => {
                            if (tool === 'search') vm.focusSearch();
                            else vm.setOpenFilter(tool);
                        }}
                    >
                        {tool === 'search' ? (
                            <svg
                                viewBox="0 0 24 24"
                                width="12"
                                height="12"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden="true"
                            >
                                <circle cx="11" cy="11" r="7" />
                                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                            </svg>
                        ) : (
                            <svg
                                viewBox="0 0 24 24"
                                width="12"
                                height="12"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden="true"
                            >
                                <path d="M4 5h16l-6 7.5V18l-4 2v-7.5z" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                </>
            )}
        </span>
    );
}

function rowActionItems(
    vm: RoleMapTableVM,
    group: RoleMapGroup,
    t: TFunction
): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];
    if (vm.bulkEnabled) {
        items.push({
            key: 'resync',
            label: t('console.roleMap.table.actionResync'),
            onSelect: () => vm.resyncGroup(group),
        });
        items.push({
            key: 'to-default',
            label: t('console.roleMap.table.actionToDefault'),
            onSelect: () => vm.setGroupScope(group, 'default'),
        });
    }
    // EAA-1817: "Đổi vai trò" re-points via `PATCH /role-maps/{id}` — that
    // endpoint is `IsSuperAdmin`-only server-side (see
    // `useRoleMapWrites.repointMutation`'s docstring), so this is gated on
    // `canManageDefault` regardless of the current scope, not
    // `canManageCurrentScope`. Restricted to single-id groups: a multi-role
    // mapping has no single unambiguous id to re-point without first asking
    // WHICH chip, which this row action does not model.
    if (vm.canManageDefault && group.ids.length === 1) {
        items.push({
            key: 'repoint',
            label: t('console.roleMap.table.actionRepoint'),
            onSelect: () => vm.openRepoint(group),
        });
    }
    // EAA-1817: the +/- diff against the ecosystem default only means anything
    // while looking at an org's own override slice.
    if (vm.scope === 'org' && vm.actingOrgId) {
        items.push({
            key: 'scope-diff',
            label: t('console.roleMap.table.actionScopeDiff'),
            onSelect: () => vm.openScopeDiff(group),
        });
    }
    items.push({
        key: 'delete',
        label:
            group.ids.length > 1
                ? t('console.roleMap.table.actionDeleteMany', { count: group.ids.length })
                : t('console.roleMap.table.actionDeleteOne'),
        danger: true,
        disabled: !vm.canManageCurrentScope,
        onSelect: () => vm.removeGroup(group),
    });
    return items;
}

/**
 * EAA-1837: the data table of `role-map-table.html`. ONE ROW PER
 * `(role_key, application)` GROUP — a multi-role mapping renders its two-plus
 * chips in the single "Vai trò trong ứng dụng" cell, and its checkbox selects
 * every underlying `RoleMap` id (see `RoleMapGroup`'s docstring for the
 * EAA-1828 bug class that makes this non-negotiable).
 *
 * The checkbox column is DISABLED whenever `vm.bulkEnabled` is false — in the
 * "Mặc định" scope no bulk endpoint can touch the rows at all, so offering a
 * selection that could only ever produce a `skipped` response would be a lie.
 *
 * EAA-1884: rebuilt on the kit `DataTable` (was a hand-rolled `<table>`),
 * matching the same move already made for `apps/AppsTable.tsx`. Row
 * selection now goes through `DataTable`'s native `rowSelection`
 * (`onSelectionChange` in `useRoleMapTable`) instead of a hand-rolled
 * checkbox column — see that hook's doc comment for why this is a clean
 * drop-in and not a re-wrap. Column visibility stays custom (`vm.isColumnVisible`
 * gating which `ColumnConfig` entries get pushed below): the "Cột" picker
 * lives in `RoleMapToolbar` with its own popover chrome, not `DataTable`'s
 * own column-config toolbar, and mixing the two would be two different
 * pickers claiming the same job.
 */
export function RoleMapTable({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const showApplication = vm.isColumnVisible('application');
    const showAppRoles = vm.isColumnVisible('app_roles');
    const showScope = vm.isColumnVisible('scope');
    const showStatus = vm.isColumnVisible('status');
    // EAA-1817: caller-owned config map for the shared `StatusDot` primitive —
    // same labels/tones the old `.rm-status.is-ok|is-stale` classes rendered.
    const statusConfig = useMemo<Record<'ok' | 'stale', StatusConfig>>(
        () => ({
            ok: { label: t('console.roleMap.status.normal'), tone: 'success' },
            stale: { label: t('console.roleMap.status.stale'), tone: 'error' },
        }),
        [t]
    );

    const columns = useMemo<ColumnConfig<RoleMapGroup>[]>(() => {
        const cols: ColumnConfig<RoleMapGroup>[] = [
            {
                key: 'role_key',
                title: (
                    <HeaderCell
                        label={t('console.roleMap.table.columnRoleKey')}
                        field="role_key"
                        vm={vm}
                        tool="search"
                        toolTitle={t('console.roleMap.table.filterRoleKeyTitle')}
                    />
                ),
                render: (_value, group) => <code className="rm-role-key">{group.role_key}</code>,
            },
        ];

        if (showApplication) {
            cols.push({
                key: 'application',
                title: (
                    <HeaderCell
                        label={t('console.roleMap.table.columnApplication')}
                        field="application"
                        vm={vm}
                        tool="apps"
                        toolTitle={t('console.roleMap.table.filterApplicationTitle')}
                    />
                ),
                render: (_value, group) => (
                    <span className="rm-app-cell">
                        {/* The registry code can be longer than the 26px tile;
                            the tile shows the first three characters and the
                            full code stays in the tooltip. */}
                        <span className="rm-app-cell__tile" title={group.application}>
                            {group.application.slice(0, 3)}
                        </span>
                        {group.appName}
                    </span>
                ),
            });
        }

        if (showAppRoles) {
            cols.push({
                key: 'app_roles',
                title: (
                    <HeaderCell
                        label={t('console.roleMap.table.columnAppRoles')}
                        vm={vm}
                        tool="search"
                        toolTitle={t('console.roleMap.table.filterAppRolesTitle')}
                    />
                ),
                render: (_value, group) => (
                    <span className="rm-app-roles">
                        {group.appRoles.map((chip) => (
                            <Chip key={chip.id} tone={chip.stale ? 'stale' : 'mapped'}>
                                {chip.app_role}
                            </Chip>
                        ))}
                    </span>
                ),
            });
        }

        cols.push({
            key: 'accounts',
            title: (
                <HeaderCell
                    label={t('console.roleMap.table.columnAccounts')}
                    field="accounts"
                    vm={vm}
                />
            ),
            // EAA-1884: `.rm-accounts-cell` only sets `font-variant-numeric` —
            // a span carries it fine now that `DataTable` owns the `<td>`.
            render: (_value, group) => <span className="rm-accounts-cell">{group.accountCount}</span>,
        });

        if (showScope) {
            cols.push({
                key: 'scope',
                title: <HeaderCell label={t('console.roleMap.table.columnScope')} vm={vm} />,
                render: (_value, group) => (
                    <span className="rm-scope-cell">
                        {group.organization_id === null
                            ? t('console.roleMap.table.scopeDefault')
                            : (vm.actingOrgName ?? t('console.roleMap.table.scopeOrgFallback'))}
                    </span>
                ),
            });
        }

        if (showStatus) {
            cols.push({
                key: 'status',
                title: (
                    <HeaderCell
                        label={t('console.roleMap.table.columnStatus')}
                        vm={vm}
                        tool="status"
                        toolTitle={t('console.roleMap.table.filterStatusTitle')}
                    />
                ),
                render: (_value, group) => (
                    <StatusDot
                        className="rm-status"
                        status={group.isStale ? 'stale' : 'ok'}
                        config={statusConfig}
                    />
                ),
            });
        }

        return cols;
        // WHY: `vm` (the whole hook result) is a fresh object every render of
        // `useRoleMapTable`, same as every other consumer in this file
        // (`HeaderCell`/`rowActionItems` already take it whole) — there is no
        // narrower stable dependency to list without threading a dozen
        // individual fields through, and this table was never memoized
        // against `vm` before this conversion either.
    }, [t, vm, showApplication, showAppRoles, showScope, showStatus, statusConfig]);

    /**
     * EAA-1884: header checkbox state/indeterminate/disabled, and the
     * "select all" click cycle, are now entirely antd's — `getCheckboxProps`
     * disabling every row the same way the old per-row `disabled` prop did is
     * what makes antd auto-disable the header checkbox too (its own
     * `useSelection` hook disables "select all" when every row on the page is
     * individually disabled, or the page is empty), which is exactly
     * `!vm.bulkEnabled || vm.pageGroups.length === 0` without re-deriving it.
     */
    const rowSelection = useMemo<TableRowSelection<RoleMapGroup>>(
        () => ({
            selectedRowKeys: Array.from(vm.selectedKeys),
            onChange: vm.onSelectionChange,
            columnWidth: 40,
            getCheckboxProps: (record) => ({
                disabled: !vm.bulkEnabled,
                'aria-label': t('console.roleMap.table.selectRowAria', {
                    roleKey: record.role_key,
                    appName: record.appName,
                }),
            }),
            getTitleCheckboxProps: () => ({
                'aria-label': t('console.roleMap.table.selectAllAria'),
            }),
        }),
        [vm.selectedKeys, vm.onSelectionChange, vm.bulkEnabled, t]
    );

    const rowClassName = useCallback(
        (group: RoleMapGroup) => {
            const selected = vm.selectedKeys.has(group.key);
            // Stale wins over selected on purpose — the export's librarian row
            // is both, and renders the danger tone (see 14-role-map.css).
            return ((selected ? 'is-selected' : '') + (group.isStale ? ' is-stale' : '')).trim();
        },
        [vm.selectedKeys]
    );

    return (
        <div className="portal-table-scroll">
            <DataTable<RoleMapGroup>
                className="rm-datatable"
                data={vm.pageGroups}
                columns={columns}
                rowKey="key"
                pagination={false}
                rowSelection={rowSelection}
                rowClassName={rowClassName}
                actions={(group) => (
                    <DropdownMenu
                        items={rowActionItems(vm, group, t)}
                        triggerAriaLabel={t('console.roleMap.table.rowActionsAria', {
                            roleKey: group.role_key,
                            appName: group.appName,
                        })}
                    />
                )}
                actionsWidth={44}
            />
        </div>
    );
}
